import axios from "axios";
import { store } from "./App/store.js";
import { clearPermissions } from "./features/permissions/permissionSlice.js";
import { logout, refreshSuccess } from "./features/auth/authSlice.js";
import { getAccessToken } from "./Util/authStorage.js";
import { noteApiCall } from "./Util/analytics.js";

const url = process.env.REACT_APP_BACKEND_URL;

// Every request is bounded: on a flaky shop-floor connection a hung request
// used to leave its loading toast up forever. Generous enough for heavy reports
// and uploads; a call that legitimately needs longer passes its own `timeout`.
export const REQUEST_TIMEOUT_MS = 120000;

const client = axios.create({ baseURL: url, withCredentials: true, timeout: REQUEST_TIMEOUT_MS });

// Usage analytics (Util/analytics.js): time every request for the current page
// view. Registered first so it sees the raw response before the unwrap below.
client.interceptors.request.use((config) => {
    config.metadata = { ...(config.metadata || {}), startedAt: Date.now() };
    return config;
});
client.interceptors.response.use(
    (response) => {
        noteApiCall(response.config?.metadata?.startedAt, true);
        return response;
    },
    (error) => {
        if (!axios.isCancel(error)) noteApiCall(error?.config?.metadata?.startedAt, false);
        return Promise.reject(error);
    },
);

const newIdempotencyKey = () =>
    (typeof crypto !== "undefined" && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}-${Math.random().toString(36).slice(2)}`;

// ── Request interceptor — always attach the freshest access token ─────────────
client.interceptors.request.use((config) => {
    const token = getAccessToken();
    if (!config.headers) config.headers = {};
    if (token && token.length > 5) {
        config.headers.Authorization = `Bearer ${token}`;
    } else {
        delete config.headers.Authorization;
    }
    // Every POST carries an Idempotency-Key. It lives on the config, so the
    // 401-refresh retry below resends the SAME key and the server replays the
    // first result instead of creating a duplicate. Forms that must survive a
    // double submit pass their own key (hooks/useIdempotencyKey.js).
    if (String(config.method || "").toLowerCase() === "post" && !config.headers["Idempotency-Key"]) {
        config.headers["Idempotency-Key"] = newIdempotencyKey();
    }
    return config;
});

// ── Silent-refresh state ──────────────────────────────────────────────────────
// Prevents multiple concurrent refresh calls when several requests 401 at once.
let isRefreshing = false;
let failedQueue = [];

const processQueue = (error, token = null) => {
    failedQueue.forEach((prom) => {
        if (error) {
            prom.reject(error);
        } else {
            prom.resolve(token);
        }
    });
    failedQueue = [];
};

export const doLogout = () => {
    store.dispatch(logout());
    store.dispatch(clearPermissions());
    window.location.href = "/session-expired";
};

// ── Shared silent-refresh core ────────────────────────────────────────────────
// Calls the refresh endpoint directly (bare axios, no interceptor loop). The
// HttpOnly refresh cookie is sent because withCredentials is set. On success it
// persists the new access token (and the slid session expiry) into Redux.
// Used by BOTH the reactive 401 interceptor below and the proactive-refresh hook.
// Throws on failure so the caller decides whether to hard-logout.
//
// Single-flight: concurrent callers within this tab (e.g. the proactive timer
// firing while a 401 also triggers a refresh) share ONE network round-trip, so
// we never race two rotations against the same cookie.
// A refresh failure is only "session is dead → give up" when the endpoint
// returns a definitive auth rejection (400/401). Everything else — a network
// error / timeout / CORS-shaped failure (no response), a 429 (e.g. a rate-limit
// blip on a burst of tab refreshes), or a 5xx — is transient and worth retrying.
const isTransientRefreshError = (err) => {
    const status = err?.response?.status;
    if (status === undefined) return true; // network / timeout / CORS — no response
    return status === 429 || status >= 500;
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const REFRESH_MAX_ATTEMPTS = 3;
const REFRESH_BASE_DELAY_MS = 300;

let inFlightRefresh = null;
export function silentRefresh() {
    if (inFlightRefresh) return inFlightRefresh;

    inFlightRefresh = (async () => {
        try {
            let lastErr;
            for (let attempt = 1; attempt <= REFRESH_MAX_ATTEMPTS; attempt++) {
                try {
                    const resp = await axios.post(
                        `${url}login/refresh`,
                        {},
                        { withCredentials: true },
                    );
                    const newAccessToken = resp.data?.data?.accessToken;
                    if (!newAccessToken) throw new Error("No access token in refresh response");

                    // Only the access token is updated — session expiry is absolute
                    // (fixed at login) so the countdown is never touched by a refresh.
                    store.dispatch(refreshSuccess({ token: newAccessToken }));
                    return newAccessToken;
                } catch (err) {
                    lastErr = err;
                    // Give up immediately on a definitive auth rejection so the
                    // caller can hard-logout; only retry transient failures.
                    if (attempt >= REFRESH_MAX_ATTEMPTS || !isTransientRefreshError(err)) {
                        throw err;
                    }
                    // Exponential backoff with jitter (~300ms, ~600ms).
                    const delay = REFRESH_BASE_DELAY_MS * 2 ** (attempt - 1);
                    await sleep(delay + Math.random() * 150);
                }
            }
            throw lastErr;
        } finally {
            inFlightRefresh = null;
        }
    })();

    return inFlightRefresh;
}

// A refresh failure is only "session is dead → log out" when the refresh
// endpoint itself returns a definitive auth rejection (400/401 — cookie
// missing, expired, revoked). A network error / timeout / 5xx means the
// backend is momentarily unreachable (e.g. mid-deploy or restart); the session
// is almost certainly still valid, so we must NOT log the user out.
export const isDefiniteAuthFailure = (err) => {
    const status = err?.response?.status;
    return status === 400 || status === 401;
};

// ── Response interceptor ──────────────────────────────────────────────────────
client.interceptors.response.use(
    (response) => {
        if (process.env.NODE_ENV === "development") {
            console.log("API Response:", response);
        }
        if (response.data?.success) {
            return response.data.data;
        } else if (response.status >= 200 && response.status < 300) {
            return response;
        }
        if (response.data?.success === false) {
            return Promise.reject({
                statusCode: response.status,
                message: response.data?.message ?? "Request failed",
                errors: response.data?.errors ?? [],
            });
        }
        return response.data;
    },
    async (error) => {
        // Ignore intentional cancellations
        if (
            axios.isCancel(error) ||
            error?.code === "ERR_CANCELED" ||
            error?.name === "CanceledError" ||
            error?.message === "canceled"
        ) {
            return Promise.reject({
                name: "CanceledError",
                code: "ERR_CANCELED",
                message: "Request cancelled",
            });
        }

        if (process.env.NODE_ENV === "development") {
            console.error("API Error:", error);
        }

        const originalRequest = error.config;

        // ── Silent refresh on 401 ─────────────────────────────────────────────
        // The HttpOnly refresh cookie is sent automatically by the browser
        // because withCredentials is set on the client. If the cookie is
        // missing/expired, the refresh call itself 401s and we hard-logout.
        if (error.response?.status === 401 && !originalRequest._retry) {

            if (isRefreshing) {
                // Queue this request until the ongoing refresh resolves
                return new Promise((resolve, reject) => {
                    failedQueue.push({ resolve, reject });
                })
                    .then((newToken) => {
                        originalRequest.headers.Authorization = `Bearer ${newToken}`;
                        return client(originalRequest);
                    })
                    .catch((err) => Promise.reject(err));
            }

            originalRequest._retry = true;
            isRefreshing = true;

            try {
                const newAccessToken = await silentRefresh();

                processQueue(null, newAccessToken);
                originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;
                return client(originalRequest);
            } catch (refreshErr) {
                processQueue(refreshErr, null);
                // Only hard-logout on a definitive auth rejection. Transient
                // failures (backend restarting / offline / 5xx) must preserve
                // the session — the call simply fails and can be retried once
                // the backend is reachable again.
                if (isDefiniteAuthFailure(refreshErr)) {
                    doLogout();
                }
                return Promise.reject(normalizeError(refreshErr));
            } finally {
                isRefreshing = false;
            }
        }

        return readBlobError(error).then((e) => Promise.reject(normalizeError(e)));
    },
);

// Downloads/PDFs ask for responseType "blob", so an error body is a Blob and
// the server's message ("Access denied …") was lost behind "Request failed
// with status code 403". Read it back into data (JSON, or plain text).
function readBlobError(error) {
    const data = error?.response?.data;
    if (typeof Blob === "undefined" || !(data instanceof Blob) || typeof data.text !== "function") {
        return Promise.resolve(error);
    }
    return data.text().then((text) => {
        let parsed = text;
        try { parsed = JSON.parse(text); } catch { /* not JSON */ }
        error.response.data = parsed;
        return error;
    }, () => error);
}

// An nginx error page (413 Request Entity Too Large, 502 …) is HTML: never
// show markup as a toast.
const isHtml = (v) => typeof v === "string" && /^\s*<(!doctype|html|head|body)/i.test(v);

function normalizeError(error) {
    // No response at all: timed out, offline, DNS/CORS. Say which, in plain words,
    // instead of axios' "Network Error" / "timeout of 120000ms exceeded".
    if (!error.response) {
        const timedOut = error.code === "ECONNABORTED" || error.code === "ETIMEDOUT" || /timeout/i.test(error.message || "");
        return {
            statusCode: undefined,
            message: timedOut
                ? "The server took too long to respond. Check your connection and try again."
                : "Unable to reach the server. Check your internet connection.",
            errors: [],
            details: null,
            code: error.code,
            name: error.name,
            network: true,
            timedOut,
        };
    }
    return {
        statusCode: error.response?.status,
        message:
            error.response?.data?.message ??
            error.response?.data?.error ??
            (typeof error.response?.data === "string" && !isHtml(error.response.data) ? error.response.data : null) ??
            (isHtml(error.response?.data) ? `The server returned an error (${error.response.status}${error.response.statusText ? ` ${error.response.statusText}` : ""}).` : null) ??
            error.message ??
            "Network or server error",
        errors: error.response?.data?.errors ?? [],
        // Structured error payload (e.g. the challan credit gate's needsApproval/tier).
        details: error.response?.data?.details ?? null,
        code: error.code,
        name: error.name,
    };
}

export default client;
